import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { ClientSidebar } from "@/components/sidebars";
import { useAuth } from "@/lib/auth";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TIPOS = [
  { id: "grua",       label: "Grúa / Servicio vial", icon: "fas fa-truck-pickup" },
  { id: "medica",     label: "Servicio médico telefónico", icon: "fas fa-stethoscope" },
  { id: "ambulancia", label: "Envío de ambulancia",    icon: "fas fa-ambulance" },
  { id: "legal",      label: "Servicio legal",       icon: "fas fa-gavel" },
];

const ESPERAS_INFO = [
  "Grúa / asistencia vial: 30 días",
  "Consulta médica: 10 días",
  "Legal (problemas automovilísticos): 10 días",
  "Legal (otros casos): 30 días",
  "Ambulancia: 30 días",
];

export default function SolicitarAsistencia() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const beneficiarios = user?.beneficiarios || [];
  const [tipo, setTipo] = useState("grua");
  const [legalSubtipo, setLegalSubtipo] = useState("automovilistico");
  const [beneficiario_index, setBenefIdx] = useState(0);
  const [descripcion, setDesc] = useState("");
  const [ubicacion, setUbic] = useState("");
  const [telefono_contacto, setTel] = useState("");
  const [loading, setLoading] = useState(false);
  const [carencias, setCarencias] = useState([]);

  useEffect(() => {
    apiClient.get("/solicitudes/carencias").then(({ data }) => setCarencias(data.carencias || [])).catch(() => {});
  }, []);

  const carenciaKey = tipo === "legal" ? (legalSubtipo === "automovilistico" ? "legal_automovilistico" : "legal_otro") : tipo;
  const carencia = carencias.find((c) => c.key === carenciaKey);
  const bloqueado = carencia && !carencia.disponible;
  const fechaDisponible = bloqueado ? new Date(carencia.disponible_desde).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }) : null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/solicitudes", {
        tipo, beneficiario_index, descripcion, ubicacion, telefono_contacto,
        legal_subtipo: tipo === "legal" ? legalSubtipo : undefined,
      });
      toast.success("Solicitud enviada. Te contactaremos de inmediato.");
      navigate("/cuenta/historial");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout sidebar={ClientSidebar}>
      <header className="app-header">
        <h1>Solicitar Servicio</h1>
        <p className="text-muted">Activa el servicio para tu familiar en México. Respondemos en minutos.</p>
      </header>

      {user?.membership_status !== "active" && (
        <div className="alert-card"><i className="fas fa-exclamation-triangle" />
          <div><strong>Tu membresía no está activa.</strong>
            <p>Activa un plan para poder solicitar servicio.</p></div>
        </div>
      )}

      {beneficiarios.length === 0 ? (
        <div className="panel"><p>Primero agrega beneficiarios.</p></div>
      ) : (
        <form onSubmit={submit} className="panel">
          <h3>1. Tipo de servicio</h3>
          <div className="tipo-grid">
            {TIPOS.map((t) => (
              <button type="button" key={t.id} onClick={() => setTipo(t.id)}
                className={`tipo-btn ${tipo === t.id ? "active" : ""}`} data-testid={`tipo-${t.id}`}>
                <i className={t.icon} />{t.label}
              </button>
            ))}
          </div>

          {tipo === "legal" && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>Tipo de asunto legal *</label>
              <select value={legalSubtipo} onChange={(e) => setLegalSubtipo(e.target.value)} data-testid="sol-legal-subtipo">
                <option value="automovilistico">Problema automovilístico (espera: 10 días)</option>
                <option value="otro">Otro caso legal (espera: 30 días)</option>
              </select>
            </div>
          )}

          {bloqueado && (
            <div className="alert-card" style={{ marginTop: 14 }} data-testid="carencia-aviso">
              <i className="fas fa-clock" />
              <div>
                <strong>Este servicio aún está en periodo de espera.</strong>
                <p>{carencia.label} requiere {carencia.dias} días desde la activación de tu membresía. Estará disponible a partir del <strong>{fechaDisponible}</strong></p>
              </div>
            </div>
          )}

          <h3>2. Beneficiario</h3>
          <div className="form-group">
            <select value={beneficiario_index} onChange={(e) => setBenefIdx(Number(e.target.value))} data-testid="sol-benef">
              {beneficiarios.map((b, i) => (
                <option key={i} value={i}>{b.nombre} — {b.relacion} ({b.municipio}, {b.estado})</option>
              ))}
            </select>
          </div>

          <h3>3. Detalles</h3>
          <div className="form-group"><label>Descripción del problema *</label>
            <textarea required rows={4} value={descripcion} onChange={(e) => setDesc(e.target.value)} placeholder="Ej. mi mamá tiene fiebre alta y no responde bien" data-testid="sol-desc" />
          </div>
          <div className="form-grid-2">
            <div className="form-group"><label>Ubicación actual (si difiere del domicilio)</label>
              <input type="text" value={ubicacion} onChange={(e) => setUbic(e.target.value)} placeholder="Carretera, km, calle, etc." data-testid="sol-ubic" /></div>
            <div className="form-group"><label>Teléfono de contacto inmediato</label>
              <input type="tel" value={telefono_contacto} onChange={(e) => setTel(e.target.value)} placeholder="Si es distinto al registrado" data-testid="sol-tel" /></div>
          </div>
          <button type="submit" disabled={loading || bloqueado} className="btn btn-primary btn-block" data-testid="sol-submit">
            {bloqueado ? `Disponible a partir del ${fechaDisponible}` : loading ? "Enviando..." : "Enviar solicitud"}
          </button>

          <p className="text-muted" style={{ fontSize: 12, marginTop: 14 }} data-testid="carencias-info">
            <i className="fas fa-info-circle" /> <strong>Periodos de espera desde la activación de tu membresía:</strong>{" "}
            {ESPERAS_INFO.join(" · ")}.
          </p>
        </form>
      )}
    </AppLayout>
  );
}
